import { useRef, useEffect, useCallback, useContext } from 'react';
import { Track, TrackCallback, TrackCategory } from '@sunbit-dev/purchase-common/src/services/networkService/models';
import { datadogRum } from '@datadog/browser-rum';
import mixpanel from 'mixpanel-browser';
import { checkIfKnownError } from '@sunbit-dev/purchase-common/src/helpers/logs';
import { generateString } from '@sunbit-dev/purchase-common/src/utilities/string';
import { AppContext } from '~/contexts/appContext';
import { logsService } from '~/services/logsService';
import { sampling } from '@sunbit-dev/common/services/sampling';

export const useTrack = () => {
  const { error, retailer, isPs1Associated } = useContext(AppContext) || {};

  const addContextError = (eventName: string, args) => {
    datadogRum.addRumGlobalContext(`error-${eventName}-${generateString()}`, args);
  };

  useEffect(() => {
    datadogRum.addRumGlobalContext('app', { error, retailer, isPs1Associated });
  }, [error, retailer, isPs1Associated]);

  // Keep payload up to date
  const basePayload = useRef({});
  basePayload.current = { error, retailer, isPs1Associated };

  const track: TrackCallback = useCallback(
    ({ eventName, type, props, datadogInfo = { type: TrackCategory.Action } }: Track) => {
      const _type = type || datadogInfo.type;

      const fullPayload = { ...logsService.info, ...props };

      if (_type === TrackCategory.Sample) {
        return sampling?.event(eventName, fullPayload);
      }

      // Always logging to mixpanel
      mixpanel.track(eventName, fullPayload);

      if (datadogInfo.type === TrackCategory.Action) {
        datadogRum.addAction(eventName || 'General', props);
      } else if (datadogInfo.type === TrackCategory.Error) {
        const errorCode = datadogInfo?.errorInfo?.code;
        const isKnownError = checkIfKnownError(errorCode);
        if (isKnownError) {
          return datadogRum.addAction(eventName || 'General', {
            ...props,
            isKnownError: true,
          });
        } else {
          addContextError(eventName, props);
          datadogRum.addError(new Error(eventName || 'General'), props);
        }
      }
    },
    [] // Should always stay empty
  );

  return { track };
};
